import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import StudentTimesheetEntry from "../../api/models/studentTimesheetEntry";
import TaskType from "../../api/models/tasktype";
import { getTaskTypes } from "../../api/services/taskTypeService";
import { getEsyStudents, getStudentsForEntry } from "../../api/services/timesheetService";
import { EntryTypes, TaskTypeLookup } from "../../common/constants";
import { validateEntryMinutesForDay, validateOnTaskTypeChange } from "./shared";
import { daysOfWeek, groupTaskEntries, isEntryValid, removeDuplicatesManyProperties } from '../../common/utils';
import DayEntry from "./types/dayEntry";
import TimesheetTaskEntries from "./types/timesheetTaskEntries";
import Step2Props from "./types/step2.child";
import TimesheetWeekTask from "../../api/models/TimesheetWeekTask";
import WeekItem from "../../api/models/timesheetWeekItem";
import Modal from "../modal";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

/**
 * Returns the students available based on the options selected. 
 */
const loadStudents = async ({ disciplineId, schoolId, startDate, endDate, districtId, isEsy }: any) => {
    return isEsy ? getEsyStudents(disciplineId, districtId, startDate) : getStudentsForEntry(disciplineId, schoolId, startDate, endDate);
}

/**
 * Entry options for students
 */
const EntryForStudent = ({
    entryState,
    setEntryState,
    editNote,
    setHasChanges,
    hasChanges,
    setIsSaved,
    isSaved,
    setEntryTasks,
    setStudentIds,
    setShowSavedMessage,
    selectedDistrictProgramOption
}: Step2Props) => {
    //selection
    const [studentId, setStudentId] = useState<number>(entryState.studentId ?? -1);
    const [openHasChangesModal, setOpenHasChangesModal] = useState<boolean>(false);
    const [changeStudentId, setChangeStudentId] = useState<number | undefined>(undefined);
    //options
    const [students, setStudents] = useState<StudentTimesheetEntry[]>(entryState.students ?? []);
    const [student, setStudent] = useState<StudentTimesheetEntry>();
    const [caseloadId, setCaseloadId] = useState<number>();
    const [taskTypes, setTaskTypes] = useState<TaskType[]>((entryState.taskTypes ?? []).sort((a, b) => a.name.localeCompare(b.name)).sort(x => x.hasRecoupment ? -1 : 1));
    const [noStudents, setNoStudents] = useState<boolean>(false);
    const [recoupment, setRecoupment] = useState<boolean>(entryState.recoupment ?? false);
    const [timesheetEntryTasks, setTimesheetEntryTasks] = useState<TimesheetTaskEntries[]>(entryState.timesheetEntryTasks ?? []);

    //validation
    const [validationMsg, setValidationMsg] = useState<string | null | JSX.Element>();
    /**
     * Loads the students on page load (promiseFn)
     */
    useAsync({
        promiseFn: loadStudents,
        disciplineId: entryState.disciplineId,
        startDate: entryState.startDate,
        endDate: entryState.endDate,
        schoolId: entryState.schoolId,
        districtId: entryState.district?.districtId,
        isEsy: entryState.isEsy,
        onResolve: (res) => {
            setStudents(res.data);
            entryState.students = res.data;
            if (res.data.length === 0)
                setNoStudents(true);
        }
    });

    /**
     * When the parent component save function is finished, this effect will run
     */
    useEffect(() => {
        if (isSaved) {
            resetEntries();
            setTaskTypes([]);
            setStudentId(-1);
            setStudentIds && setStudentIds([-1])
            setIsSaved(false);
            entryState.entryTypeId = EntryTypes.Student;
            setEntryState(entryState);
        }
    }, [isSaved]);

    /**
     * Resets the entries
     */
    const resetEntries = () => {
        //Reset entries
        entryState.timesheetEntryTasks = [];
        setTimesheetEntryTasks([]);
        setEntryTasks && setEntryTasks([]);
        setEntryState(entryState);
    }

    /**
     * When the student is changed, save any changes and select the new student
     * @param studentId 
     */
    const studentChanged = async (studentId: number, ignoreChanges: boolean = false) => {
        setShowSavedMessage!(false);
        setChangeStudentId(studentId);
        if (hasChanges && !ignoreChanges) {
            setOpenHasChangesModal(true);
            return;
        }

        //if we have pending changes, save them.
        /*if (hasChanges) {
            const didSave = await saveEntry();
            if (didSave) {
                setHasChanges(false);
            }
        }*/

        //If the student id is not valid, reset the entries
        if (studentId === -1) {
            setTimesheetEntryTasks([]);
            setEntryTasks && setEntryTasks([]);
            return;
        }

        //update the selected student
        setStudentId(studentId);
        setStudentIds && setStudentIds([studentId])
        const s = students.find(student => student.studentId === studentId);
        if (s !== undefined) {
            setStudent(s);
            setCaseloadId(s.caseLoadId);
        }

        //Set the items saved message for this screen
        entryState.studentName = s?.studentName;

        entryState.studentId = studentId;
        var student = students?.find(s => s.studentId === studentId);
        setValidationMsg('');
        entryState.hasValidationErrors = false;

        //update the task types based on the student selected
        if (!entryState.editingEntry) { //we don't change the task type when editing
            //First, get the task types for this student and discipline
            getTaskTypes(entryState.disciplineId, student?.studentStatus?.studentStatusId ?? -1, EntryTypes.Student).then(r => {
                let taskTypes = (r?.data?.taskTypes?.filter(t => t.isVisible) ?? [])
                    ?.sort((a, b) => a.name.localeCompare(b.name));

                if (taskTypes && taskTypes.length > 0) {
                    taskTypes = taskTypes?.sort((a, b) => taskTypes.find(x => x.taskTypeId === a.taskTypeId)?.hasRecoupment ? -1 : 1)
                }

                //Set the task types hook and entryState
                setTaskTypes(taskTypes.sort((a, b) => a.name.localeCompare(b.name)));
                entryState.taskTypes = taskTypes;

                //Now we will generate the timesheet entries per task type
                if (studentId > 0) {
                    if (!entryState.selectedTimesheet) {
                        return;
                    }

                    //Find all entries for this student
                    const tasks = entryState.selectedTimesheet?.items
                        ?.filter(x => x.students?.includes(studentId) && x.schoolId === entryState.schoolId) //Filter on only this student
                        ?.flatMap(x => x.tasks)
                        ?.filter(x => x.programId === (entryState.programId && entryState.programId > 0 ? entryState.programId : null)) ?? []; //Flatten the tasks and return that

                    //Loop through each item and generate the timesheet entries
                    const taskTypeEntries: TimesheetTaskEntries[] = [];
                    tasks.forEach(task => groupTaskEntries(task, entryState, taskTypes).forEach(entry => taskTypeEntries.push(entry)));

                    //Set the timesheet entry tasks
                    setTimesheetEntryTasks(taskTypeEntries);
                    setEntryTasks && setEntryTasks(taskTypeEntries);
                    entryState.timesheetEntryTasks = taskTypeEntries;
                }
            });
        }
        setHasChanges(false);
    }

    /**
     * If the selected timesheet does not have the item in question, add the item
     */
    const addTimesheetItem = (entryTask: TimesheetTaskEntries | undefined) => {
        if (!entryTask) return;

        //Check to see if the task exists in the timesheet
        if (!entryState.selectedTimesheet?.items?.some(x => x?.tasks.some(y => y.taskTypeId === entryTask.taskTypeId))) {
            //Create the task
            const tasks: TimesheetWeekTask[] = [{
                taskTypeName: entryTask.taskType?.taskTypeName ?? '',
                taskTypeId: entryTask.taskTypeId ?? 0,
                entryTypeName: entryState.entryTypeName ?? '',//entryTask.entryTypeName,
                entryTypeId: entryState.entryTypeId ?? 0,//entryTask.entryTypeId,
                programName: entryState.programName ?? '',//entryTask.programName,
                programId: entryState.programId,//entryTask.programId,
                schoolId: entryState.schoolId ?? 0,
                schoolName: entryState.school?.name ?? '',
                entries: entryTask.entries
            }];

            //Create the item
            const tmpItem: WeekItem = {
                title: `${student?.studentName}`,
                subTitle: entryState.schoolName ?? '',
                description: entryState.studentName ?? '',
                districtId: entryState.districtId ?? 0,
                schoolId: entryState.schoolId ?? 0,
                students: [studentId],
                billToId: entryTask.billToId,
                tasks: tasks,
                day: entryState.startDate,
            };

            //Make sure the items array is actually an array
            if (entryState.selectedTimesheet && !entryState.selectedTimesheet.items) {
                entryState.selectedTimesheet.items = [];
            }

            setEntryState(entryState => {
                const tmpEntryState = { ...entryState };

                //Add the temp item
                if (tmpEntryState.selectedTimesheet && !tmpEntryState.selectedTimesheet.items.includes(tmpItem)) {
                    tmpEntryState.selectedTimesheet.items.push(tmpItem);
                }

                return tmpEntryState;
            });
        }
    }

    /**
     * Syncs the selected timesheet with the new entries
     */
    const syncTimesheetEntries = () => {
        //Loop through our current tasks and update any existing entries
        timesheetEntryTasks.map(entryTask => {
            //For each task, find the entry on our selected timesheet
            entryState.selectedTimesheet?.items
                ?.filter(x => x.students?.length === 1 && x.students?.includes(studentId))
                ?.map(item => {
                    item?.tasks?.map(task => {
                        if (task.taskTypeId === entryTask.taskTypeId && (entryTask.isRecoupment ? task.entries.some(x => x.recoupment) : true) && (entryTask.isMakeupTime ? task.entries.some(x => x.makeupTime) : true)) {
                            task.entries = entryTask.entries;
                            return task;
                        }
                        return task;
                    })
                    return item;
                })
        });
    }


        /**
         * Check for existing entries that matches the options selected, no duplicated entries! 
         * @param tempEntries 
         * @returns 
         */
        const syncExistingEntries = () => {
            setTimesheetEntryTasks(current => {
                //Loop through each entry and find it's corresponding value
                current.map(entryTask => {
                    if (entryTask.entries.some(x => x.minutes !== undefined && x.minutes > 0)) {
                        return;
                    }
    
                    //clear old values
                    entryTask.entries.map((entry) => {
                        entry.notes = "";
                        entry.minutes = undefined;
                        return entry;
                    });
    
                    //make sure we have a selected timesheet
                    if (typeof entryState.selectedTimesheet === "undefined")
                        return;
    
                    var schoolId = entryState.schoolId;
    
                    entryTask.entries.map(entry => {
                        if (entryState.selectedTimesheet) {
                            var item = entryState.selectedTimesheet.items?.find(i =>
                                i.districtId === entryState.districtId &&
                                i.students?.[0] === entryState.studentId &&
                                i.schoolId === schoolId);
    
                            var task = item?.tasks?.find(t =>
                                t.entryTypeId === entryState.entryTypeId &&
                                t.taskTypeId === entryTask.taskTypeId &&
                                t.isMakeupTime === entryTask.isMakeupTime &&
                                t.schoolId === schoolId &&
                                t.programId === (((entryState.programId ?? 0) <= 0) ? null : entryState.programId));
    
                            //look for matching entries and update the temp entries to match them, users can't create duplicate ones! 
                            if (typeof task !== "undefined") {
                                var date = moment(entry.dateOfEntry ?? entry.date);
                                var tempEntry = task.entries.find(e => date.isSame(e.dateOfEntry ?? e.date, 'day'));
    
                                if (tempEntry) {
                                    entry.notes = tempEntry.notes?.valueOf();
                                    entry.minutes = tempEntry.minutes;
                                    entry.timesheetEntryId = tempEntry.entryId?.valueOf();
                                }
                            }
                        }
    
                        return entry;
                    });
                });
                return current;
            });
            setEntryState(entryState => {
                const tmpEntryState = { ...entryState };
                tmpEntryState.timesheetEntryTasks = timesheetEntryTasks;
                return tmpEntryState;
            });
        }
    
    /**
     * When the user selects a task type, add that task type to the selected task types
     * @param taskTypeId 
     * @param isRecoupment 
     */
    const taskTypeChanged = async (taskTypeId: number, isRecoupment: boolean = false, isMakeupTime: boolean = false) => {
        const entryTasks: TimesheetTaskEntries[] = [];
        let isRemove = timesheetEntryTasks.some(x => x.taskTypeId === taskTypeId && (!isRecoupment ? !x.isRecoupment : x.isRecoupment === isRecoupment) && (!isMakeupTime ? !x.isMakeupTime : x.isMakeupTime === isMakeupTime));
        //Toggle the selected task type
        setTimesheetEntryTasks(current => {
            //Find the entry task
            const entryTask = current.find(x => x.taskTypeId === taskTypeId && (!isRecoupment ? !x.isRecoupment : x.isRecoupment === isRecoupment) && (!isMakeupTime ? !x.isMakeupTime : x.isMakeupTime === isMakeupTime));

            let tmpEntries: TimesheetTaskEntries[] = [];

            //If the entry task exists, then remove it from the array
            if (entryTask) {
                current.forEach(x => {
                    if (x.taskTypeId === taskTypeId) {
                        const tmpRecoupment = x.isRecoupment === isRecoupment;
                        const tmpMakeupTime = x.isMakeupTime === isMakeupTime;
                        if (tmpRecoupment && tmpMakeupTime) return x;
                    }

                    tmpEntries.push(x);
                    return x;
                });
                // return tmpEntries;
            }
            else {

                //Otherwise, generate the blank entries and add the task type to the array
                let newEntryTask = createNewEntryTask(taskTypeId, isRecoupment, isMakeupTime);
                entryTasks.push(newEntryTask);

                tmpEntries = [...current, newEntryTask]
                    .sort((a) => (a.taskType.name ?? '') === (a.taskType.name ?? '') ? -1 : 1)
                    .sort((a) => a.isRecoupment ? -1 : 1)
                    .sort((a) => a.isMakeupTime ? -1 : 1);
            }
            setValidationMsg(validateOnTaskTypeChange(tmpEntries, entryState));
            return tmpEntries;
        });

        if (isRemove) {
            entryState.timesheetEntryTasks = entryState?.timesheetEntryTasks?.filter(x => x.taskTypeId != taskTypeId);
        } else {
            let newEntryTask = createNewEntryTask(taskTypeId, isRecoupment, isMakeupTime);
            entryState?.timesheetEntryTasks?.push(newEntryTask);
        }
        setEntryState(entryState);

        entryTasks.forEach(entryTask => addTimesheetItem(entryTask));
        setEntryTasks && setEntryTasks(entryTasks);

        syncExistingEntries();
        //toggle(undefined, true);
    }

    const createNewEntryTask = (taskTypeId: number, isRecoupment: boolean = false, isMakeupTime: boolean = false): TimesheetTaskEntries => {
        const tempEntries: DayEntry[] = [];
        daysOfWeek(entryState.startDate).forEach((day) => {
            //If the student is not set, use the studentId to find it
            let s = student;
            if (!student) {
                s = students.find(student => student.studentId === studentId);
                setStudent(s);
            }

            let taskType = entryState?.taskTypes?.find(x => x.taskTypeId === taskTypeId);

            tempEntries.push({
                dateOfEntry: day,
                notes: "",
                minutes: taskTypeId === TaskTypeLookup.Absence || taskTypeId === TaskTypeLookup.Unavailable ? undefined : 0,
                enabled: day.isSameOrAfter(s?.startDate) && (s?.endDate === null || day.isSameOrBefore(s?.endDate)),
                recoupment: isRecoupment,
                makeupTime: isMakeupTime,
            });
        });
        const newEntryTask: TimesheetTaskEntries = {
            startDate: entryState.startDate,
            endDate: entryState.endDate,
            schoolId: entryState.schoolId,
            taskType: taskTypes.find(y => y.taskTypeId === taskTypeId),
            taskTypeId,
            isRecoupment,
            isMakeupTime,
            entries: tempEntries
        };

        return newEntryTask;
    }

    /**
     * Updates the minutes when a time entry is added/changed
     * @param index 
     * @param inputMinutes 
     * @param taskEntries 
     */
    const updateEntryTime = (index: number, inputMinutes: number, taskTypeId: number, isRecoupment: boolean = false, isMakeupTime: boolean = false) => {
        //Get the entry task
        const entryTaskSearch = timesheetEntryTasks.find(x => x.taskTypeId === taskTypeId && (!isRecoupment ? !x.isRecoupment : x.isRecoupment === isRecoupment) && (!isMakeupTime ? !x.isMakeupTime : x.isMakeupTime === isMakeupTime));
        const entryTask = Array.isArray(entryTaskSearch) ? entryTaskSearch[0] : entryTaskSearch;

        //If none was found, return early
        if (!entryTask) {
            return;
        }

        const isValid = isEntryValid(timesheetEntryTasks, index, inputMinutes);

        //if there is a validation error users can't add more time to other entries, but they are allowed to reduce, otherwise they can't change anything
        if (entryState.hasValidationErrors && !isValid) {
            alert("You have validation errors, please fix those issues before adjusting other entries");
            return;
        }


        //Loop through the entry tasks and find the one we are updating
        const entryTasks = [...timesheetEntryTasks];
        entryTasks.map(x => {
            if (x.taskTypeId === taskTypeId && (!isRecoupment ? !x.isRecoupment : x.isRecoupment === isRecoupment) && (!isMakeupTime ? !x.isMakeupTime : x.isMakeupTime === isMakeupTime)) {
                if (x.entries[index] !== undefined) {
                    x.entries[index].minutes = inputMinutes;
                }
                return x;
            }

            return x;
        });

        //syncExistingEntries(entryTask.entries, taskTypeId);
        //syncTimesheetEntries();

        //Update our hook and entryState with the updated tasks
        setTimesheetEntryTasks(entryTasks);
        entryState.timesheetEntryTasks = entryTasks;
        setEntryTasks && setEntryTasks(entryTasks);
        //Set our entry state
        setEntryState(entryState);
        //check's validation, set's the validation message to the result of the validation
        setValidationMsg(validateEntryMinutesForDay(entryState, entryTask.entries, inputMinutes));

        //Set our entry state
        //setEntryState(entryState);
        //toggle(true, true);
        setHasChanges(true);
    }

    /**
     * Toggle handler for  "Absence" or "Unavailable entries". They don't have time associated with them
     * @param index 
     */
    const toggleNonTimeEntries = (index: number, taskTypeId?: number) => {
        const entryTasks = [...timesheetEntryTasks];

        entryTasks.map(x => {
            if (x.taskTypeId === taskTypeId) {

                if (x.entries[index] !== undefined && x.entries[index].minutes === 0)
                    x.entries[index].minutes = undefined;
                else
                    x.entries[index].minutes = 0;
            }
            return x;
        });

        //Update our hook and entryState with the updated tasks
        setTimesheetEntryTasks(entryTasks);
        setEntryTasks && setEntryTasks(entryTasks);
        entryState.timesheetEntryTasks = entryTasks;

        //Set our entry state
        setEntryState(entryState);
        setHasChanges(true);
    }

    /**
     * Displays the notes when the edit link is clicked
     * @param index number
     * @param taskTypeId number
     */
    const editNoteClicked = (index: number, taskTypeId?: number, isRecoupment: boolean = false, isMakeupTime: boolean = false) => {
        //Verify we have a task type before continuing
        if (!taskTypeId) return;

        //Get entries
        const searchEntries = (timesheetEntryTasks.filter(x => x.taskTypeId === taskTypeId && x.isRecoupment === isRecoupment && x.isMakeupTime === isMakeupTime)[0]?.entries as unknown) as DayEntry[] ?? [];

        //Reset the state of the validtion errors, they will be checked again with this component loads
        // entryState.hasValidationErrors = false;
        // setValidationMsg(null);

        //Update the task type id so our notes know if we have selections
        setEntryState(entryState => {
            const tmpEntryState = { ...entryState };
            tmpEntryState.taskTypeId = taskTypeId
            return tmpEntryState;
        });

        //Load the edit note dialog
        editNote({
            dayEntry: searchEntries![index],
            entryModel: entryState,
            taskTypeId,
            isRecoupment,
            isMakeupTime
        });
        setHasChanges(true);
    }

    const isDateEnabled = (date: moment.Moment): boolean => {
        if (!student) {
            let s = entryState.students?.find(x => x.studentId === studentId);
            if (!s)
                return false;

            setStudent(s);
        }

        let startDate = moment(student?.startDate!);
        let endDate = null;

        if (student?.endDate)
            endDate = moment(student?.endDate);

        return startDate.isSameOrBefore(date) && (!endDate || endDate?.isSameOrAfter(date));
    }

    /**
     * This will revalidate each entry after the user edit notes
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setValidationMsg(null);
        timesheetEntryTasks.map(entryTask => {
            const entries = entryTask.entries;

            for (var i = 0; i < entries.length; i++) {
                if (entries[i].minutes !== undefined) {
                    var msg = validateEntryMinutesForDay(entryState, entries, entries[i].minutes!);
                    if (msg != null) {
                        setValidationMsg(msg);
                        break;
                    }
                }
            }
        });
    }, []);

    return (
        <Fragment>
            <Modal
                saveText='Okay'
                open={openHasChangesModal}
                onCancel={() => {
                    setOpenHasChangesModal(false);
                }}
                onSave={() => {
                    setOpenHasChangesModal(false);
                    studentChanged(changeStudentId!, true);
                }}
                showButtons
            >
                Data has been entered but not saved. Are you sure you would like to change the student?<br /><strong>Warning:</strong> All unsaved data will be lost.
            </Modal>
            {/* We need students to show the options, display message if none exist. */}
            {noStudents && <div className="text-sm text-red-900">The selected school doesn't have any students active for this week, please select another school</div>}

            {/**
             * Task Types
             */}
            {!noStudents && students.length > 0 && <>
                <label>Select Student</label>
                <Select
                    value={
                      studentId === -1 ? null : removeDuplicatesManyProperties(students, ['studentId', 'startDate', 'endDate']).map((student) => ({ value: student.studentId, label: `${student.studentName} (${moment(student.startDate).format("MMMM D")} - ${student.endDate !== null ? moment(student.endDate).format("MMMM D") : "none"})` })).find(o => o.value === studentId) ?? null
                    }
                    onChange={(option: any) => studentChanged(option ? Number(option.value) : -1)}
                    styles={graySelectStyles}
                    isSearchable={false}
                    placeholder="Select Student"
                    options={removeDuplicatesManyProperties(students, ['studentId', 'startDate', 'endDate']).map((student) => ({ value: student.studentId, label: `${student.studentName} (${moment(student.startDate).format("MMMM D")} - ${student.endDate !== null ? moment(student.endDate).format("MMMM D") : "none"})` }))}
                />

                {taskTypes.length > 0 &&
                    <div className="task-type-boxes">
                        <label>Task Type</label><br />
                        {taskTypes
                            ?.sort((a, b) => a.name.localeCompare(b.name))
                            ?.sort((a, b) => a.hasRecoupment ? -1 : 1)
                            ?.sort((a, b) => a.hasMakeupTime ? -1 : 1)
                            ?.map((taskType, i) => <>
                                <div className={`w-${taskType.hasRecoupment ? '1/4' : taskType.hasMakeupTime ? '1/3' : 'full'} inline-block`} key={`task-option-label-${taskType.taskTypeId}`}>
                                    <label className={`inline-block`} htmlFor={`task-option-${taskType.taskTypeId}`}>
                                        <input
                                            id={`task-option-${taskType.taskTypeId}`}
                                            name={`task-option-${taskType.taskTypeId}`}
                                            key={`task-option-checkbox-${taskType.taskTypeId}`}
                                            type="checkbox" value={taskType.taskTypeId}
                                            checked={timesheetEntryTasks.filter(x => x.taskTypeId === taskType.taskTypeId && !x.isRecoupment && !x.isMakeupTime).length > 0}
                                            onChange={(e) => taskTypeChanged(Number(e.target.value))}
                                            disabled={!studentId || studentId < 1}
                                        /> {taskType.name}
                                    </label>
                                </div>

                                {/* Only show this option if the task type has a recoupment */}
                                {taskType.hasRecoupment &&
                                    <div className="inline-block w-2/5" key={`task-option-label-${taskType.taskTypeId}-recoupment`}>
                                        <label className="inline-block" htmlFor={`task-option-${taskType.taskTypeId}-recoupment`}>
                                            <input
                                                id={`task-option-${taskType.taskTypeId}-recoupment`}
                                                name={`task-option-${taskType.taskTypeId}-recoupment`}
                                                key={`task-option-checkbox-${taskType.taskTypeId}-recoupment`}
                                                type="checkbox"
                                                value={taskType.taskTypeId}
                                                checked={timesheetEntryTasks.filter(x => x.taskTypeId === taskType.taskTypeId && x.isRecoupment && !x.isMakeupTime).length > 0}
                                                onChange={(e) => taskTypeChanged(Number(e.target.value), true)}
                                            /> {taskType.name} Recoupment
                                        </label>
                                    </div>
                                }

                                {/* Only show this option if the task type has a makeup time option set */}
                                {taskType.hasMakeupTime &&
                                    <div className="inline-block w-1/3" key={`task-option-label-${taskType.taskTypeId}-makeupTime`}>
                                        <label className="inline-block" htmlFor={`task-option-${taskType.taskTypeId}-makeupTime`}>
                                            <input
                                                id={`task-option-${taskType.taskTypeId}-makeupTime`}
                                                name={`task-option-${taskType.taskTypeId}-makeupTime`}
                                                key={`task-option-checkbox-${taskType.taskTypeId}-makeupTime`}
                                                type="checkbox"
                                                value={taskType.taskTypeId}
                                                checked={timesheetEntryTasks.filter(x => x.taskTypeId === taskType.taskTypeId && !x.isRecoupment && x.isMakeupTime).length > 0}
                                                onChange={(e) => taskTypeChanged(Number(e.target.value), false, true)}
                                            /> {taskType.name} Makeup Time
                                        </label>
                                    </div>
                                }
                            </>)}
                    </div>
                }
            </>}

            {/**
             * Entries Container - Displays the entries below the Task Types
             */}
            <div className={`entries-container overflow-x-hidden drop-shadow-lg ${timesheetEntryTasks.length > 0 ? 'max-h-96 block' : 'hidden'} ${timesheetEntryTasks.length >= 4 ? 'overflow-scroll scrollbar' : 'overflow-none'} mt-8`}>
                {/* Loop through the task types and display the entries for those task types */}
                {timesheetEntryTasks
                    //?.sort((a, b) => a.taskType.name.localeCompare(b.taskType.name))
                    //?.sort((a, b) => taskTypes.find(x => x.taskTypeId === a.taskTypeId)?.hasRecoupment ? -1 : 1)
                    .map(entryTask => {
                        if (!entryTask.entries) entryTask.entries = [];
                        if (entryTask.entries.length === 0) {
                            const tempEntries: DayEntry[] = [];
                            daysOfWeek(entryState.startDate).forEach((day) => {
                                tempEntries.push({
                                    dateOfEntry: day,
                                    notes: "",
                                    minutes: entryTask.taskTypeId === TaskTypeLookup.Absence || entryTask.taskTypeId === TaskTypeLookup.Unavailable ? undefined : 0,
                                    enabled: day.isSameOrAfter(student?.startDate) && (student?.endDate === null || day.isSameOrBefore(student?.endDate)),
                                });
                            });
                            entryTask.entries = tempEntries;
                        }

                        //Get the task type
                        const tt = taskTypes.find(x => x.taskTypeId === entryTask.taskType?.taskTypeId);
                        //Begin row of text boxes
                        return (
                            <div className="entryTaskContainer">
                                <h3 className="block">
                                    {`${tt?.name}`}
                                    {entryTask?.isRecoupment ? " Recoupment" : ""}
                                    {entryTask?.isMakeupTime ? " Makeup Time" : ""}
                                </h3>
                                <div className="flex flex-row w-full mt-2 justify-evenly">
                                    {entryTask.entries.map((entry, i) => {
                                        const dateOfEntry = moment(entry.dateOfEntry ?? entry.date);
                                        const isWeekend = dateOfEntry.isoWeekday() === 6 || dateOfEntry.isoWeekday() === 7;
                                        if (isWeekend && !entryState.district?.enableWeekendBilling) return null;
                                        const isEnabled = (entry.enabled ?? true) && !moment().isBefore(dateOfEntry);
                                        const payrateExists = selectedDistrictProgramOption?.payrates!.some(pr => {
                                            if (!pr.payrateStartDate) return false;
                                            const start = moment(pr.payrateStartDate);
                                            const end = pr.payrateEndDate ? moment(pr.payrateEndDate) : moment();
                                            if (!start.isValid() || !end.isValid()) return false;
                                            return dateOfEntry.isBetween(start, end, undefined, '[]');
                                        }) ?? false;
                                        //Begin text box
                                        return (
                                            <div
                                                className={`w-20 text-center entry-option-${studentId}-${tt?.taskTypeId}-${entry.timesheetEntryId ?? i}`}
                                                key={`entry-option-${studentId}-${tt?.taskTypeId}-${entry.timesheetEntryId ?? i}-${dateOfEntry && moment(dateOfEntry).isValid() ? moment(dateOfEntry).format("MD") : null}`}
                                            >
                                                {/* Day of week */}
                                                <div className={`font-bold ${(!isEnabled || ((dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) == false) || moment(students.find(x => x.studentId === studentId)?.endDate).isBefore(dateOfEntry) || !isDateEnabled(dateOfEntry)) ? "text-gray-300" : ""}`}>
                                                    {moment(dateOfEntry).isValid() ? moment(dateOfEntry).format('ddd') : ''}
                                                </div>

                                                {/* Date */}
                                                <div className={`text-xs mb-2 ${(!isEnabled || ((dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) == false) || moment(students.find(x => x.studentId === studentId)?.endDate).isBefore(dateOfEntry) || !isDateEnabled(dateOfEntry)) ? "text-gray-300" : ""}`}>
                                                    {moment(dateOfEntry).isValid() ? moment(dateOfEntry).format('M/D') : ''}
                                                </div>

                                                {/* Absence/Unavailable Checkbox */}
                                                <div key={`entry-row-task-type-${tt?.taskTypeId}-${studentId}`}>
                                                    {(tt?.taskTypeId === TaskTypeLookup.Unavailable || tt?.taskTypeId === TaskTypeLookup.Absence) && (
                                                        <input type="Checkbox"
                                                            key={`entry-row-task-type-${tt?.taskTypeId}-${studentId}-checkbox`}
                                                            checked={entry?.minutes !== undefined}
                                                            onChange={(e) => toggleNonTimeEntries(i, tt?.taskTypeId)}
                                                            disabled={(!isEnabled || ((dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) == false) || moment(students.find(x => x.studentId === studentId)?.endDate).isBefore(dateOfEntry) || !isDateEnabled(dateOfEntry))} />
                                                    )}

                                                    {/* Minutes Text Box */}
                                                    {!(tt?.taskTypeId === TaskTypeLookup.Unavailable || tt?.taskTypeId === TaskTypeLookup.Absence) && (
                                                        <input
                                                            className={`inline-block text-center rounded-md h-12 w-12 appearance-none border mb-1 ${(isEnabled && ((dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) == true)) ? "border-black" : "border-slate-100 bg-slate-900"}`}
                                                            key={`entry-row-task-type-${tt?.taskTypeId}-${studentId}-textbox`}
                                                            value={entry?.minutes ? entry.minutes.valueOf() : "" ?? ""}
                                                            onChange={(e) => updateEntryTime(i, Number(e.target.value.replace(/\D/g, "")), entryTask.taskTypeId ?? 0, entryTask.isRecoupment ?? false, entryTask.isMakeupTime ?? false)}
                                                            disabled={(!isEnabled || ((dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) == false) || moment(students.find(x => x.studentId === studentId)?.endDate).isBefore(dateOfEntry) || !isDateEnabled(dateOfEntry))} />
                                                    )}

                                                    {/* Notes Link */}
                                                    {
                                                        (tt?.taskTypeId === TaskTypeLookup.Unavailable || tt?.taskTypeId === TaskTypeLookup.Absence ?
                                                            entry.minutes !== undefined :
                                                            entry.minutes !== undefined && entry.minutes > 0) &&
                                                        (dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists) &&
                                                        !(tt?.taskTypeId === TaskTypeLookup.Direct && !entryTask.isRecoupment && !entryTask.isMakeupTime) &&
                                                        <div
                                                            key={`entry-row-task-type-${tt?.taskTypeId}-${studentId}-actions`}
                                                            className="cursor-pointer text-ptsblue-dark"
                                                            onClick={(e) => editNoteClicked(i, tt?.taskTypeId, entryTask.isRecoupment, entryTask.isMakeupTime ?? false)}
                                                        >{(entry.notes.length === 0) ? "Add Note" : "Edit Note"}</div>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
            </div>

            {validationMsg != null && typeof validationMsg === "string" && (
                <div className="text-sm text-red-900">{validationMsg}</div>
            )}

            {validationMsg != null && typeof validationMsg !== "string" && validationMsg}
        </Fragment>
    );
}

export default EntryForStudent;